(function () {
  'use strict';

  const config = window.returnsConfig;
  if (!config) return;

  const returnsRoot = document.querySelector('.returns');
  const intro = document.getElementById('ReturnsIntro');
  const form = document.getElementById('ReturnsForm');
  const loading = document.getElementById('ReturnsLoading');
  const pluginContainer = document.getElementById('ReturnsPlugin');
  const submitBtn = document.getElementById('ReturnsForm-submit');
  const orderInput = document.getElementById('ReturnsForm-order');
  const emailInput = document.getElementById('ReturnsForm-email');
  const errorBanner = document.getElementById('ReturnsForm-error');
  const errorMessage = document.getElementById('ReturnsForm-error-message');

  if (!form || !submitBtn) return;

  let isSubmitting = false;
  let pluginErrorObserver = null;

  submitBtn.addEventListener('click', handleSubmit);

  // Allow submit via Enter key
  [orderInput, emailInput].forEach((input) => {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleSubmit();
    });
  });

  async function handleSubmit() {
    if (isSubmitting) return;
    isSubmitting = true;
    clearErrors();

    const orderNumber = orderInput.value.trim();
    const emailOrZip = emailInput.value.trim();

    // Client-side validation
    let hasError = false;
    if (!orderNumber) {
      showFieldError('ReturnsForm-order-error', orderInput);
      hasError = true;
    } else if (!orderNumber.toLowerCase().startsWith('#cf')) {
      showFieldError('ReturnsForm-order-format-error', orderInput);
      hasError = true;
    }
    if (!emailOrZip) {
      showFieldError('ReturnsForm-email-error', emailInput);
      hasError = true;
    } else if (!isValidEmailOrZip(emailOrZip)) {
      showFieldError('ReturnsForm-email-format-error', emailInput);
      hasError = true;
    }
    if (hasError) {
      isSubmitting = false;
      return;
    }

    // Show loading, hide form
    form.classList.add('hidden');
    loading.classList.remove('hidden');
    pluginContainer.classList.add('hidden');
    if (returnsRoot) returnsRoot.classList.remove('returns--plugin-active');
    errorBanner.classList.add('hidden');

    try {
      const response = await fetch(config.proxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber: orderNumber }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok || !data.countryCode) {
        throw new Error(data.error || 'not_found');
      }

      const resolved = resolveParcelLabLang(data.countryCode.toLowerCase());
      const countryCode = resolved.countryCode;
      const langCode = resolved.langCode;

      injectParcelLab(countryCode, langCode, orderNumber, emailOrZip);
    } catch (err) {
      loading.classList.add('hidden');
      form.classList.remove('hidden');

      const msg = err.message === 'not_found' ? config.i18n.errorNotFound : config.i18n.errorGeneric;
      showError(msg);
    } finally {
      isSubmitting = false;
    }
  }

  function resolveParcelLabLang(countryCode) {
    let langMap = {};
    try {
      langMap = typeof config.langMap === 'string' ? JSON.parse(config.langMap) : config.langMap || {};
    } catch (e) {
      langMap = {};
    }

    // Country unknown to our map: fall back to English UI (matches the Focal
    // reference; parcelLab gets countryCode='en' and langCode='en').
    if (!(countryCode in langMap)) {
      return { countryCode: 'en', langCode: 'en' };
    }

    // Mapping value is "<target_country>:<target_lang>" — split so an order's
    // country can be remapped to a different parcelLab portal (e.g. AT → DE).
    const [targetCountry, targetLang] = String(langMap[countryCode]).split(':');
    const langCode = config.langOverride ? targetLang : config.shopLocale || 'en';
    return { countryCode: targetCountry, langCode: langCode };
  }

  // parcelLab keeps session state in window.location.hash; on (re)mount it
  // reads the hash and deep-links to the result-view, skipping the lookup
  // form. Reset before any render so autofill finds the inputs it needs.
  function resetParcelLabRouteState() {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  function injectParcelLab(countryCode, langCode, orderNumber, emailOrZip) {
    resetParcelLabRouteState();

    // Dedupe CSS link
    const cssId = 'parcellab-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = config.parcelLabCss;
      document.head.appendChild(link);
    }

    // (Re)create plugin element
    const pluginDiv = document.createElement('div');
    pluginDiv.id = 'pl-returns-plugin';
    pluginDiv.setAttribute('data-user', config.parcelLabUserId);
    pluginDiv.setAttribute('data-lang-code', langCode);
    pluginDiv.setAttribute('data-country-code', countryCode);
    pluginContainer.innerHTML = '';
    pluginContainer.appendChild(pluginDiv);

    // Dedupe script — parcelLab only auto-renders on initial script load, so on
    // repeat submits we must manually re-render against the freshly recreated
    // #pl-returns-plugin div, otherwise the new container stays empty until our
    // 15s timeout fires.
    const scriptId = 'parcellab-script';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      if (typeof window.renderReturnsPlugin === 'function') {
        window.renderReturnsPlugin({ forceRender: true });
        waitForPluginAndAutofill(orderNumber, emailOrZip);
        return;
      }
      if (existingScript.dataset.status === 'loading') {
        // Script tag exists but hasn't executed yet (rapid resubmit during initial load).
        existingScript.addEventListener(
          'load',
          function () {
            if (typeof window.renderReturnsPlugin === 'function') {
              window.renderReturnsPlugin({ forceRender: true });
            }
            waitForPluginAndAutofill(orderNumber, emailOrZip);
          },
          { once: true }
        );
        existingScript.addEventListener(
          'error',
          function () {
            showPluginError(config.i18n.errorGeneric);
          },
          { once: true }
        );
        return;
      }
      // Previous load failed (or finished without exposing renderReturnsPlugin) —
      // drop the stale tag so the fresh-load path below can actually retry.
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.dataset.status = 'loading';
    script.src = config.parcelLabScript;
    script.onload = function () {
      script.dataset.status = 'loaded';
      waitForPluginAndAutofill(orderNumber, emailOrZip);
    };
    script.onerror = function () {
      script.dataset.status = 'error';
      showPluginError(config.i18n.errorGeneric);
    };
    document.body.appendChild(script);
  }

  function waitForPluginAndAutofill(orderNumber, emailOrZip) {
    const TIMEOUT = 15000;
    const inputSelector = 'input[type="text"], input:not([type])';

    // Plugin may render inputs synchronously — check once before observing
    const existing = pluginContainer.querySelectorAll(inputSelector);
    if (existing.length >= 2) {
      autofillAndSubmit(existing, orderNumber, emailOrZip);
      return;
    }

    let timeoutId;
    const observer = new MutationObserver((_, obs) => {
      const inputs = pluginContainer.querySelectorAll(inputSelector);
      if (inputs.length >= 2) {
        clearTimeout(timeoutId);
        obs.disconnect();
        autofillAndSubmit(inputs, orderNumber, emailOrZip);
      }
    });

    observer.observe(pluginContainer, { childList: true, subtree: true });

    timeoutId = setTimeout(() => {
      observer.disconnect();
      const inputs = pluginContainer.querySelectorAll(inputSelector);
      if (inputs.length < 2) {
        showPluginError(config.i18n.errorTimeout);
      }
    }, TIMEOUT);
  }

  function autofillAndSubmit(inputs, orderNumber, emailOrZip) {
    setNativeValue(inputs[0], orderNumber);
    setNativeValue(inputs[1], emailOrZip);

    // Wait 500ms (matching old project delay) for React to process, then submit
    setTimeout(() => {
      const submitButton = pluginContainer.querySelector('button[type="submit"], button');
      if (submitButton) {
        submitButton.click();
      }

      // Reveal plugin only after auto-fill + auto-submit so the user lands on the
      // parcelLab post-submit view, not the intermediate blank lookup form.
      loading.classList.add('hidden');
      if (intro) intro.classList.add('hidden');
      pluginContainer.classList.remove('hidden');

      // Expand the returns container out of page-width--narrow so the plugin's
      // post-submit tables (e.g. Bestellinformationen) have room to render.
      if (returnsRoot) returnsRoot.classList.add('returns--plugin-active');

      // Watch for parcelLab errors
      watchForPluginErrors();
    }, 500);
  }

  /**
   * Set value on a React-controlled input using the native setter
   * so React's internal state picks up the change.
   */
  function setNativeValue(input, value) {
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    // Dispatch events to help 3rd party plugins (like ParcelLab) track state correctly
    input.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    input.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
    input.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));

    nativeSetter.call(input, value);

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new FocusEvent('focusout', { bubbles: true }));
    input.dispatchEvent(new FocusEvent('blur', { bubbles: true }));
  }

  function watchForPluginErrors() {
    const ERROR_WATCH_TIMEOUT = 30000;

    // Clean up any previous observer from a prior submission
    if (pluginErrorObserver) pluginErrorObserver.disconnect();

    function forceErrorsVisible() {
      pluginContainer
        .querySelectorAll('.pl-input-wrapper--error .pl-input-message--error')
        .forEach((msg) => {
          if (msg.style.visibility === 'hidden') {
            msg.style.visibility = 'visible';
          }
        });
    }

    pluginErrorObserver = new MutationObserver(forceErrorsVisible);
    pluginErrorObserver.observe(pluginContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    setTimeout(forceErrorsVisible, 1500);

    setTimeout(() => {
      if (pluginErrorObserver) {
        pluginErrorObserver.disconnect();
        pluginErrorObserver = null;
      }
    }, ERROR_WATCH_TIMEOUT);
  }

  function isValidEmailOrZip(value) {
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    var zipRegex = /^\d{4,10}$/;
    return emailRegex.test(value) || zipRegex.test(value);
  }

  function showFieldError(errorId, input) {
    const el = document.getElementById(errorId);
    if (el) el.classList.remove('hidden');
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorId);
    }
  }

  function clearErrors() {
    document.querySelectorAll('.returns__field-error').forEach((el) => el.classList.add('hidden'));
    [orderInput, emailInput].forEach((input) => {
      input.setAttribute('aria-invalid', 'false');
      input.removeAttribute('aria-describedby');
    });
    errorBanner.classList.add('hidden');
  }

  function showError(msg) {
    errorMessage.textContent = msg;
    errorBanner.classList.remove('hidden');
  }

  function showPluginError(msg) {
    loading.classList.add('hidden');
    pluginContainer.classList.add('hidden');
    if (returnsRoot) returnsRoot.classList.remove('returns--plugin-active');
    if (intro) intro.classList.remove('hidden');
    form.classList.remove('hidden');
    showError(msg);
  }
})();
